/**
 * The app shell: MDR's console inside `@bluesphere/shell`, the header every
 * BlueSphere app shares (task 276, design r33 "The shared shell"; Story 53).
 *
 * Until task 276 this file held MDR's own two-row header (task 201): the
 * logo, the section pills that fold into More, the account avatar, and the
 * Client switcher in row two. The shell was cut from that header (its
 * source comment says so), so what is left here is data: which sections,
 * which Clients, who is signed in, and a `renderLink` on TanStack Router.
 * No token, API client or router reaches the shell.
 *
 * What the shell adds: the **AppSwitcher**, fed by Identity's catalog
 * (`useAppTiles`), with every link built by identity-client's `appLink`, so
 * the other app signs in with no screen (Identity design r31 §SO item 2).
 * MDR's catalog ID marks the current app.
 *
 * Row two is the shell's Client switcher (0.3.0's readable tree: the find
 * box, group headings, MSP and Client markers), given the rows
 * `clientOptions` builds from the caller's tenants and tree. Its label is
 * `Viewing` for a user who sees a tree of organizations, `Client` before the
 * tree exists (0.1.3 took the label from MDR). The pill's three states
 * (task 60) are the shell's.
 *
 * The tabs read along the pipeline (task 158): Overview · Integrations ·
 * Events · Detections · Alerts · Organizations, and Users for a caller
 * holding `manage_org`. The roadmap sections (Investigation, Settings) are
 * disabled tabs, which the shell grays in the More menu and the phone menu.
 * The Alerts pill wears the unseen count (Story 25), and the beacon stays
 * MDR's. The active tab is decided here from the router state.
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuth } from 'react-oidc-context';

import { appLink, signOut as identitySignOut } from '@bluesphere/identity-client';
import { Shell, type ShellTab } from '@bluesphere/shell';

import { identityConfig } from '../auth/oidc';
import { useAppTiles } from '../auth/useAppTiles';
import { useOrgAdmin } from '../auth/useOrgAdmin';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { clientsUnder, orgLabel } from '../lib/orgs';
import { roleLine as roleLineFor } from '../lib/users';
import { clientOptions, parseSelection, selectionValue } from './clientOptions';
import { NewAlertsBeacon, useNewAlerts } from './NewAlertsBeacon';

interface LayoutProps {
  children: ReactNode;
}

/** This app's ID in Identity's catalog, marked in the AppSwitcher. */
export const CATALOG_ID = 'mdr';

interface SectionTab {
  to: string;
  label: string;
  exact?: boolean;
}

/**
 * In pipeline order: what we connect to, what comes in, what was noticed,
 * what needs a person; then who we do it for (task 69; reordered
 * 2026-09-29, task 158).
 */
const TABS: SectionTab[] = [
  { to: '/', label: 'Overview', exact: true },
  { to: '/integrations', label: 'Integrations' },
  { to: '/events', label: 'Events' },
  { to: '/detections', label: 'Detections' },
  { to: '/alerts', label: 'Alerts' },
  { to: '/organizations', label: 'Organizations' },
];

/** Shown only to a caller holding `manage_org` (Story 22; Story 48). */
const USERS_TAB: SectionTab = { to: '/users', label: 'Users' };

/** On the roadmap, not in the product: grayed in the More menu, never a pill. */
const SOON: SectionTab[] = [
  { to: '/investigation', label: 'Investigation' },
  { to: '/settings', label: 'Settings' },
];

const SECTION_TITLES: Record<string, string> = {
  '/': 'Overview',
  '/detections': 'Detections',
  '/alerts': 'Alerts',
  '/events': 'Events',
  '/organizations': 'Organizations',
  '/integrations': 'Integrations',
  '/users': 'Users',
  '/preferences': 'Preferences',
};

/**
 * The sections that are about every Client at once, not the selected one.
 * Users are Simulint staff, not any one Client's; Preferences cover every
 * Client in the caller's scope.
 */
const ALL_CLIENTS_SECTIONS = new Set(['/organizations', '/integrations', '/users', '/preferences']);

/** With no Client selected these show every Client rather than asking for one. */
const CROSS_CLIENT_SECTIONS = new Set(['/', '/alerts', '/detections']);

export function isActiveSection(pathname: string, to: string, exact?: boolean): boolean {
  if (exact) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function sectionOf(pathname: string): string {
  const first = '/' + (pathname.split('/')[1] ?? '');
  return first === '/' ? '/' : first;
}

/** The shell's tabs for a path: the sections, Users when held, the roadmap entries disabled. */
export function shellTabs(
  pathname: string,
  opts: { usersTab: boolean; alertsBadge: string | null },
): ShellTab[] {
  const sections = opts.usersTab ? [...TABS, USERS_TAB] : TABS;
  return [
    ...sections.map((t) => ({
      to: t.to,
      label: t.label,
      active: isActiveSection(pathname, t.to, t.exact),
      badge: t.to === '/alerts' ? opts.alertsBadge : null,
    })),
    ...SOON.map((t) => ({ to: t.to, label: t.label, disabled: true })),
  ];
}

export function Layout({ children }: LayoutProps) {
  const auth = useAuth();
  const tenant = useTenant();
  const {
    selected,
    tenantId,
    orgId,
    selectedOrg,
    tenants,
    orgs,
    isLoading,
    setTenantId,
    setOrgId,
  } = useSelectedTenant();
  const orgAdmin = useOrgAdmin();
  const tiles = useAppTiles();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const newAlerts = useNewAlerts();

  const section = sectionOf(pathname);
  const sectionTitle = SECTION_TITLES[section] ?? '';
  const allClientsPage = ALL_CLIENTS_SECTIONS.has(section);

  // The browser tab says where you are and for whom (task 60), prefixed
  // with the unseen-alert count while there is one (Story 25) — the badge
  // a throttled background tab still shows.
  useEffect(() => {
    const title = sectionTitle || 'BlueSphere MDR';
    const badge = newAlerts.count > 0 ? `(${newAlerts.label}) ` : '';
    document.title = selected
      ? `${badge}${title} · ${selected.display_name} · BlueSphere MDR`
      : `${badge}${title} · BlueSphere MDR`;
  }, [sectionTitle, selected, newAlerts.count, newAlerts.label]);

  // Sign-out is global (Story 53): POST /sign-out with our token ends the
  // session in every app, then managed login's /logout.
  const signOut = () =>
    identitySignOut(() => auth.removeUser(), identityConfig, 'user', {
      accessToken: auth.user?.access_token,
    });

  const options = useMemo(() => clientOptions(tenants, orgs), [tenants, orgs]);
  const roleLine = tenant.me
    ? roleLineFor(tenant.me, orgs.homeId ? clientsUnder(orgs, orgs.homeId) : undefined)
    : undefined;

  return (
    <>
      <Shell
        product="MDR"
        currentApp={CATALOG_ID}
        apps={tiles}
        appLink={(url) => appLink(url, tenant.email || undefined)}
        tabs={shellTabs(pathname, {
          usersTab: orgAdmin.holds,
          alertsBadge: newAlerts.count > 0 ? newAlerts.label : null,
        })}
        clients={{
          options,
          selected: selectionValue({ tenantId, orgId }),
          onSelect: (value) => {
            const next = parseSelection(value);
            if (next.orgId) setOrgId(next.orgId);
            else setTenantId(next.tenantId);
          },
          emptyLabel: 'All Clients',
          loading: isLoading && options.length === 0,
        }}
        context={{
          section: sectionTitle,
          allClientsPage,
          label: orgs.homeId ? 'Viewing' : 'Client',
          hint: allClientsPage
            ? 'this page covers every Client'
            : !tenantId && !selectedOrg && CROSS_CLIENT_SECTIONS.has(section)
              ? 'showing all Clients'
              : selectedOrg && !tenantId
                ? `showing every Client of ${orgLabel(selectedOrg)}`
                : null,
        }}
        user={{
          email: tenant.email,
          displayName: tenant.displayName || undefined,
          detail: roleLine,
          to: '/preferences',
          toLabel: 'Preferences',
        }}
        onSignOut={() => void signOut()}
        renderLink={({ to, children: body, ...rest }) => (
          <Link to={to} {...rest}>
            {body}
          </Link>
        )}
      >
        {children}
      </Shell>
      <NewAlertsBeacon alerts={newAlerts} />
    </>
  );
}
